import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { startDemo } from "@/lib/demo";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, ArrowRight, Buildings, Storefront, ChartLineUp, Star, GoogleLogo, PaperPlaneTilt } from "@phosphor-icons/react";

// === HERO IMAGE — 5 alternative tecniche ===
// Cambia HERO_INDEX per scegliere:
//   0 - Blueprint architettonico con matita (tecnico, planimetrie)  [DEFAULT]
//   1 - Facciata moderna di edificio (architettura contemporanea)
//   2 - Tecnico con tablet in cantiere (sopralluogo professionale)
//   3 - Scrivania tecnica con documenti, laptop, planimetrie
//   4 - Modello architettonico 3D minimale (scala reale)
const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  "https://images.unsplash.com/photo-1487958449943-2429e8be8625?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  "https://images.unsplash.com/photo-1581094794329-c8112a89af12?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
  "https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
];
const HERO_INDEX = 0;
const HERO_IMG = HERO_IMAGES[HERO_INDEX];

export default function Landing() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/public/stats").then(r => setStats(r.data)); }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast.error("Compila tutti i campi");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/public/contact-message", {
        name: contactForm.name,
        email: contactForm.email,
        message: contactForm.message,
        subject: "Contatto da homepage",
        phone: "",
      });
      toast.success(r.data?.message || "Messaggio inviato");
      setContactForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Errore invio, riprova più tardi");
    } finally {
      setSending(false);
    }
  };

  const customerCount = stats?.customers ?? 0;
  const projectCount = stats?.completed_projects ?? 0;
  const satisfaction = stats?.satisfaction_pct ?? 99;
  const reviews = stats?.google_reviews;

  // Show N+ with soft rounding
  const displayCount = (n) => {
    if (n >= 1000) return `${Math.floor(n / 100) / 10}k+`;
    if (n >= 100) return `${Math.floor(n / 10) * 10}+`;
    if (n >= 10) return `${Math.floor(n / 5) * 5}+`;
    return n > 0 ? `${n}` : "0";
  };

  if (!loading && user) return <Navigate to={user.needs_onboarding ? "/onboarding" : "/dashboard"} replace />;

  return (
    <div>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-8 pb-12 sm:pt-16 sm:pb-20 grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 text-purple-800 text-xs font-semibold uppercase tracking-wider mb-6">
            <span className="w-2 h-2 rounded-full bg-purple-600 animate-pulse" /> Piattaforma con AI Integrata
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Gestisci Immobili <br/> <span className="ht-gradient-text">con l'AI Integrata</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
            La piattaforma gratuita per gestire immobili, documenti e pratiche immobiliari.
          </p>
          <div className="mt-8 flex gap-3 flex-wrap items-center">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-md ht-gradient border-0 text-white font-semibold shadow-lg shadow-purple-500/25" data-testid="cta-signup">
              Registrati <ArrowRight size={16} weight="bold" />
            </Link>
            <button
              type="button"
              onClick={startDemo}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border-2 border-purple-300 text-purple-700 font-semibold hover:bg-purple-50"
              data-testid="cta-explore"
            >
              {/* Iter209dn — Il CTA "Esplora" lancia la demo (POST /auth/demo-login
                  → sessione demouser → /dashboard). Stessa logica del mega-menu
                  "Esplora demo" (vedi `src/lib/demo.js`). */}
              Esplora <ArrowRight size={16} weight="bold" />
            </button>
          </div>

          {/* Real customer count + Google Reviews slot */}
          <div className="mt-10 flex items-center gap-4 flex-wrap" data-testid="landing-customers-block">
            <div className="flex -space-x-2">
              {[1,2,3,4].map(i => (
                <div key={i} className="w-9 h-9 rounded-full ht-gradient border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                  {i}
                </div>
              ))}
            </div>
            <div className="text-sm text-muted-foreground">
              <strong className="text-foreground" data-testid="customers-count">{displayCount(customerCount)}</strong> clienti soddisfatti
            </div>

            {reviews?.enabled ? (
              <a href={reviews.reviews_url || "#"} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border shadow-sm hover:shadow-md transition-shadow" data-testid="google-reviews-badge">
                <GoogleLogo size={14} weight="bold" color="#4285F4" />
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={12} weight="fill" color={i <= Math.round(reviews.average_rating || 5) ? "#F59E0B" : "#D1D5DB"} />
                  ))}
                </div>
                <span className="text-xs font-semibold">{reviews.average_rating?.toFixed(1)} · {reviews.total_reviews} recensioni</span>
              </a>
            ) : (
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border text-xs text-muted-foreground" title="Integrazione Google Reviews in arrivo">
                <GoogleLogo size={14} weight="bold" color="#4285F4" />
                <span>Recensioni Google — in arrivo</span>
              </div>
            )}
          </div>
        </div>
        <div className="relative">
          <div className="rounded-2xl overflow-hidden shadow-2xl border-8 border-white">
            <img src={HERO_IMG} alt="hero" className="w-full h-[240px] sm:h-[320px] lg:h-[400px] object-cover" />
          </div>
          <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 bg-white p-4 rounded-xl shadow-xl border max-w-xs">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="label-eyebrow">Pratica in corso · Live</span>
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#166534" /> Richiesta ricevuta</div>
              <div className="flex items-center gap-2"><CheckCircle size={14} weight="fill" color="#166534" /> Sopralluogo in corso</div>
              <div className="flex items-center gap-2 text-muted-foreground"><span className="w-3 h-3 rounded-full border-2 border-purple-500" /> Preparazione documenti</div>
              <div className="flex items-center gap-2 text-muted-foreground"><span className="w-3 h-3 rounded-full border-2 border-gray-300" /> Consegna</div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats - real numbers */}
      <section className="border-y bg-white/60">
        <div className="max-w-6xl mx-auto px-6 py-8 sm:py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            [displayCount(customerCount), "Clienti Soddisfatti"],
            // Iter207.12 — Progetti Completati mostrato esatto (non arrotondato),
            // con separatore migliaia italiano (es. 2.096).
            [projectCount.toLocaleString("it-IT"), "Progetti Completati"],
            [`${satisfaction}%`, "Tasso di Soddisfazione"],
            [stats?.support_hours || "24/7", "Supporto Disponibile"],
          ].map(([n, l]) => (
            <div key={l} data-testid={`stat-${l.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="font-display text-3xl sm:text-4xl font-bold ht-gradient-text">{n}</div>
              <div className="text-sm text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-12 sm:py-20">
        <div className="text-center mb-8 sm:mb-14">
          <div className="label-eyebrow">Ecosistema completo</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Tutto quello che ti serve per il successo</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            Una piattaforma completa che combina gestione immobiliare avanzata con accesso a servizi
            professionali di alta qualità.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {icon: Buildings, title:"Gestione Proprietà Avanzata", body:"Sistema completo per gestire il tuo portfolio immobiliare con analytics in tempo reale."},
            {icon: Storefront, title:"Servizi Professionali", body:"Prenota certificazioni, pratiche e sopralluoghi da tecnici qualificati in modo semplice e veloce."},
            {icon: ChartLineUp, title:"Dashboard Intelligente", body:"Gestisci immobili, documenti e servizi tecnici direttamente dalla piattaforma."},
          ].map(f => {
            const Ico = f.icon;
            return (
              <div key={f.title} className="kpi-card">
                <div className="w-12 h-12 rounded-xl ht-gradient flex items-center justify-center mb-4">
                  <Ico size={24} color="white" weight="duotone" />
                </div>
                <h3 className="font-display text-xl font-semibold">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <div className="ht-gradient border-0 rounded-2xl p-6 sm:p-10 text-center text-white">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Pronto a trasformare il tuo business immobiliare?</h2>
          <p className="mt-3 text-white/85">Unisciti a centinaia di professionisti che hanno già scelto Housetag.</p>
          <div className="mt-6 flex gap-3 flex-wrap justify-center items-center">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-purple-700 font-semibold shadow-lg" data-testid="cta-signup-bottom">
              Registrati <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* Iter207 — Sezione contatti in fondo alla homepage.
          Invia messaggio via /api/public/contact-message → Resend all'admin. */}
      <section
        id="contatti"
        className="scroll-mt-24 border-t bg-[color:hsl(var(--surface-alt))]"
        data-testid="landing-contact-section"
      >
        <div className="max-w-5xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="label-eyebrow">Contatti</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-3">
              Hai una domanda? <span className="ht-gradient-text">Scrivici.</span>
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Rispondiamo entro un giorno lavorativo. Se preferisci, chiamaci o
              scrivi direttamente a{" "}
              <a
                href="mailto:info@housetag.it"
                className="text-purple-700 font-semibold hover:underline"
              >
                info@housetag.it
              </a>
              .
            </p>
            <div className="mt-6 space-y-2 text-sm text-muted-foreground">
              <div>• Preventivi e informazioni sui servizi</div>
              <div>• Onboarding professionisti e team</div>
              <div>• Supporto tecnico e integrazioni</div>
            </div>
          </div>
          <form
            onSubmit={submitContact}
            className="kpi-card space-y-3"
            data-testid="landing-contact-form"
          >
            <div>
              <Label htmlFor="lcf-name">Nome</Label>
              <Input
                id="lcf-name"
                required
                value={contactForm.name}
                onChange={(e) =>
                  setContactForm({ ...contactForm, name: e.target.value })
                }
                data-testid="landing-cf-name"
              />
            </div>
            <div>
              <Label htmlFor="lcf-email">Email</Label>
              <Input
                id="lcf-email"
                type="email"
                required
                value={contactForm.email}
                onChange={(e) =>
                  setContactForm({ ...contactForm, email: e.target.value })
                }
                data-testid="landing-cf-email"
              />
            </div>
            <div>
              <Label htmlFor="lcf-msg">Messaggio</Label>
              <Textarea
                id="lcf-msg"
                rows={5}
                required
                value={contactForm.message}
                onChange={(e) =>
                  setContactForm({ ...contactForm, message: e.target.value })
                }
                data-testid="landing-cf-message"
              />
            </div>
            <Button
              type="submit"
              disabled={sending}
              className="w-full ht-gradient border-0"
              data-testid="landing-cf-submit"
            >
              {sending ? (
                "Invio..."
              ) : (
                <>
                  Invia messaggio{" "}
                  <PaperPlaneTilt size={16} weight="fill" className="ml-1" />
                </>
              )}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center pt-1">
              Inviando accetti la nostra{" "}
              <Link to="/privacy" className="underline hover:text-foreground">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
